/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { getSmartFhirCookieData } from '../Epic/smartFhirUtils';
import {
  smartFhirCookieLoginThunk,
  closeLoginErrorModal,
  doLogin,
  doLogout,
  selectIsLoggedInThroughSmartFhirCookie,
  shareVueCookieLoginThunk,
  selectIsLoggedInThroughShareVueCookie,
} from '../Login/userSlice';
import { snackNotifications } from '../notification/SnackBarUtils';
import { getShareVueCookieData } from '../ShareVue/ShareVueUtils';

export function useAuth() {
  const oktaAuthRet = useOktaAuth();
  const { authState: authState_, oktaAuth: oktaAuth_ } = oktaAuthRet || {};
  const dispatch = useAppDispatch();
  const isLoggedInThroughSmartFhirCookie = useAppSelector(selectIsLoggedInThroughSmartFhirCookie);
  const isLoggedInThroughSharevueCookie = useAppSelector(selectIsLoggedInThroughShareVueCookie);
  const oktaAuth = isLoggedInThroughSmartFhirCookie || isLoggedInThroughSharevueCookie ? undefined : oktaAuth_;
  const authState = useMemo(
    () =>
      isLoggedInThroughSmartFhirCookie || isLoggedInThroughSharevueCookie
        ? {
            isAuthenticated: true,
            isPending: false,
          }
        : authState_,
    [authState_, isLoggedInThroughSmartFhirCookie, isLoggedInThroughSharevueCookie]
  );

  const login = useCallback(
    (forceRelogin: boolean) => dispatch(doLogin({ oktaAuth, forceRelogin })),
    [dispatch, oktaAuth]
  );
  const logout = useCallback(() => {
    dispatch(closeLoginErrorModal());

    sessionStorage.removeItem('redirectUri');

    dispatch(doLogout(oktaAuth))
      .unwrap()
      .catch((e: any) => {
        snackNotifications.error(`Error logging out: ${e.message}`);
      });
  }, [dispatch, oktaAuth]);

  return {
    authState,
    login,
    logout,
  };
}

export const useSmartFhirCookieLogin = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const hasRunRef = useRef(false);

  useEffect(() => {
    // only run once
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    const smartFhirCookieData = getSmartFhirCookieData();

    dispatch(smartFhirCookieLoginThunk({ smartFhirCookieData }))
      .unwrap()
      .then((consumer_path) => {
        if (consumer_path) {
          navigate(consumer_path);
        }
      })
      .catch((e) => {
        console.error(e);
        snackNotifications.error(`Failed to login from EPIC: ${e.message}`);
      });
  }, [dispatch, navigate]);
};

export const useShareVueCookieLogin = () => {
  const dispatch = useAppDispatch();
  useEffect(() => {
    const shareVueCookieData = getShareVueCookieData();

    dispatch(shareVueCookieLoginThunk({ shareVueCookieData }))
      .unwrap()
      .then(() => {
        // do nothing
      })
      .catch((e) => {
        console.error(e);
        snackNotifications.error(`Failed to login from ShareVue: ${e.message}`);
      });
  }, [dispatch]);
};
